/**
 * /configuracion/perfil — Edición self-service del profile fiscal
 * post-onboarding.
 *
 * Diferencia con /onboarding/wizard:
 *   - Wizard: 5 pasos secuenciales, bloquea hasta completar todo.
 *   - Esta página: form único editable, save/cancel sin avanzar.
 *
 * Campos editables aquí:
 *   - Identidad: razón social, nombre comercial, NIT-DV
 *   - Dirección fiscal (depto + ciudad DANE + dirección + código postal)
 *   - Régimen tributario + responsabilidades fiscales
 *   - CIIU principal + secundarios
 *   - Branding básico: logo URL, primary color (J.4 hará upload + picker)
 *
 * NO editable aquí (requiere flow especializado):
 *   - Resolución DIAN → /configuracion/resolucion (J.3)
 *   - Certificado ECD → /configuracion/certificado (J.2.5)
 *   - Bank data + sucursales → J.2 iter 2
 *
 * Validaciones backend en facturador_profile_service.go. UI espeja
 * las críticas para feedback inmediato.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, ArrowLeft, ImagePlus, Loader2, Save, X } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import { profileApi, type UpsertProfileRequest } from '@/lib/api'
import {
  DEPARTMENTS,
  FISCAL_RESPONSIBILITIES,
  ROUTES,
  TAX_REGIMES,
} from '@/lib/constants'

type FormState = UpsertProfileRequest

// Estado inicial vacío — se llena con profileApi.get al montar.
const EMPTY: FormState = {
  legal_name: '',
  trade_name: '',
  nit_verification_digit: '',
  address: '',
  city: '',
  city_dane_code: '',
  department: '',
  department_dane_code: '',
  country: 'CO',
  postal_code: '',
  ciiu: '',
  ciiu_secondary: [],
  fiscal_responsibilities: [],
  tax_regime: '',
  logo_url: '',
  primary_color: '',
  resolution_number: '',
  resolution_prefix: '',
  resolution_from: 0,
  resolution_to: 0,
  resolution_valid_from: '',
  resolution_valid_to: '',
  resolution_technical_key: '',
  software_id: '',
}

export function ProfileEditPage() {
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [form, setForm] = useState<FormState>(EMPTY)
  const [dirty, setDirty] = useState(false)

  const profile = useQuery({
    queryKey: ['settings-profile', activeOrgId],
    queryFn: () => profileApi.get(activeOrgId!),
    enabled: !!activeOrgId,
  })

  // Hidratar form cuando llega el GET. Mantenemos los campos no
  // editables aquí (resolución, etc.) para mandar el PUT completo.
  useEffect(() => {
    if (profile.data && !dirty) {
      setForm({
        legal_name: profile.data.legal_name ?? '',
        trade_name: profile.data.trade_name ?? '',
        nit_verification_digit: profile.data.nit_verification_digit ?? '',
        address: profile.data.address ?? '',
        city: profile.data.city ?? '',
        city_dane_code: profile.data.city_dane_code ?? '',
        department: profile.data.department ?? '',
        department_dane_code: profile.data.department_dane_code ?? '',
        country: profile.data.country ?? 'CO',
        postal_code: profile.data.postal_code ?? '',
        ciiu: profile.data.ciiu ?? '',
        ciiu_secondary: profile.data.ciiu_secondary ?? [],
        fiscal_responsibilities: profile.data.fiscal_responsibilities ?? [],
        tax_regime: profile.data.tax_regime ?? '',
        logo_url: profile.data.logo_url ?? '',
        primary_color: profile.data.primary_color ?? '',
        resolution_number: profile.data.resolution_number ?? '',
        resolution_prefix: profile.data.resolution_prefix ?? '',
        resolution_from: profile.data.resolution_from ?? 0,
        resolution_to: profile.data.resolution_to ?? 0,
        resolution_valid_from: profile.data.resolution_valid_from ?? '',
        resolution_valid_to: profile.data.resolution_valid_to ?? '',
        resolution_technical_key: profile.data.resolution_technical_key ?? '',
        software_id: profile.data.software_id ?? '',
      })
    }
  }, [profile.data, dirty])

  const save = useMutation({
    mutationFn: () => profileApi.upsert(activeOrgId!, form),
    onSuccess: () => {
      toast.success('Perfil actualizado')
      qc.invalidateQueries({ queryKey: ['settings-profile', activeOrgId] })
      setDirty(false)
      nav(ROUTES.SETTINGS, { replace: true })
    },
    onError: (e: Error) => {
      toast.error(`Error: ${e.message}`)
    },
  })

  // Upload de logo. El backend devuelve la URL pública y actualiza
  // profile.logo_url. Refrescamos el form en consecuencia.
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const logoUpload = useMutation({
    mutationFn: (file: File) => profileApi.uploadLogo(activeOrgId!, file),
    onSuccess: (data) => {
      toast.success('Logo cargado')
      setForm((f) => ({ ...f, logo_url: data.logo_url }))
      qc.invalidateQueries({ queryKey: ['settings-profile', activeOrgId] })
      // El backend ya actualizó profile.logo_url — no hay dirty pendiente.
      setDirty(false)
    },
    onError: (e: Error) => {
      toast.error(`Error subiendo logo: ${e.message}`)
    },
  })

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
    setDirty(true)
  }

  function toggleResponsibility(code: string) {
    setForm((f) => {
      const list = f.fiscal_responsibilities.includes(code)
        ? f.fiscal_responsibilities.filter((c) => c !== code)
        : [...f.fiscal_responsibilities, code]
      return { ...f, fiscal_responsibilities: list }
    })
    setDirty(true)
  }

  if (authLoading || profile.isLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando perfil...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa.
        </div>
      </AppShell>
    )
  }

  if (profile.isError) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          No pude cargar el perfil. Reintenta o contacta soporte.
        </div>
      </AppShell>
    )
  }

  // Validación mínima cliente-side (espeja backend).
  const canSave =
    form.legal_name.trim().length > 0 &&
    /^[0-9]$/.test(form.nit_verification_digit) &&
    form.address.trim().length > 0 &&
    form.city.trim().length > 0 &&
    form.department.trim().length > 0 &&
    form.ciiu.trim().length === 4 &&
    form.tax_regime.length > 0 &&
    form.fiscal_responsibilities.length > 0

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center gap-2">
          <Link
            to={ROUTES.SETTINGS}
            className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg-primary"
          >
            <ArrowLeft className="h-4 w-4" /> Configuración
          </Link>
        </div>
        <h1 className="mt-2 text-3xl font-bold">Perfil del facturador</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Datos fiscales que aparecen en cada documento DIAN. Cambios se
          aplican a los próximos documentos emitidos.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (canSave) save.mutate()
          }}
          className="mt-6 space-y-6"
        >
          {/* Identidad */}
          <Section title="Identidad" description="Datos jurídicos del emisor.">
            <Field label="Razón social *" required>
              <input
                type="text"
                value={form.legal_name}
                onChange={(e) => update('legal_name', e.target.value)}
                required
                className={input}
                placeholder="Ej: SOCIEDAD COLOMBIANA DE CONSULTORIA S.A.S."
              />
            </Field>
            <Field label="Nombre comercial">
              <input
                type="text"
                value={form.trade_name ?? ''}
                onChange={(e) => update('trade_name', e.target.value)}
                className={input}
                placeholder="Si difiere de la razón social"
              />
            </Field>
            <Field
              label="Dígito de verificación (DV) *"
              required
              help="El último dígito del NIT, calculado por DIAN."
            >
              <input
                type="text"
                value={form.nit_verification_digit}
                onChange={(e) =>
                  update(
                    'nit_verification_digit',
                    e.target.value.replace(/\D/g, '').slice(0, 1),
                  )
                }
                required
                maxLength={1}
                inputMode="numeric"
                className={`${input} font-mono w-20`}
              />
            </Field>
          </Section>

          {/* Domicilio fiscal */}
          <Section title="Domicilio fiscal" description="Dirección legal registrada en RUT.">
            <Field label="Dirección *" required>
              <input
                type="text"
                value={form.address}
                onChange={(e) => update('address', e.target.value)}
                required
                className={input}
                placeholder="Carrera 11 # 93-46 Oficina 501"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Departamento *" required>
                <select
                  value={form.department}
                  onChange={(e) => update('department', e.target.value)}
                  required
                  className={input}
                >
                  <option value="">— Selecciona —</option>
                  {DEPARTMENTS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Ciudad *" required>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => update('city', e.target.value)}
                  required
                  className={input}
                  placeholder="Bogotá"
                />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Código DANE ciudad" help="5 dígitos (e.g. 11001 = Bogotá D.C.)">
                <input
                  type="text"
                  value={form.city_dane_code ?? ''}
                  onChange={(e) =>
                    update(
                      'city_dane_code',
                      e.target.value.replace(/\D/g, '').slice(0, 5),
                    )
                  }
                  inputMode="numeric"
                  className={`${input} font-mono`}
                  placeholder="11001"
                />
              </Field>
              <Field label="Código postal">
                <input
                  type="text"
                  value={form.postal_code ?? ''}
                  onChange={(e) => update('postal_code', e.target.value)}
                  className={input}
                  placeholder="110221"
                />
              </Field>
            </div>
          </Section>

          {/* Tributario */}
          <Section
            title="Régimen tributario"
            description="Define cómo Taxor calcula IVA y aplica retenciones en cada factura."
          >
            <Field label="Régimen *" required>
              <div className="mt-1.5 space-y-2">
                {TAX_REGIMES.map((r) => (
                  <label key={r.code} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="tax_regime"
                      value={r.code}
                      checked={form.tax_regime === r.code}
                      onChange={() => update('tax_regime', r.code)}
                      required
                      className="mt-1"
                    />
                    <div>
                      <span className="font-mono text-sm">{r.code}</span>{' '}
                      <span className="text-sm">— {r.label}</span>
                    </div>
                  </label>
                ))}
              </div>
            </Field>
            <Field
              label="Responsabilidades fiscales *"
              required
              help="Marca todas las que apliquen (RUT casilla 53)."
            >
              <div className="mt-1.5 space-y-2">
                {FISCAL_RESPONSIBILITIES.map((r) => (
                  <label key={r.code} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.fiscal_responsibilities.includes(r.code)}
                      onChange={() => toggleResponsibility(r.code)}
                      className="mt-1"
                    />
                    <div>
                      <span className="font-mono text-sm">{r.code}</span>{' '}
                      <span className="text-sm">— {r.label}</span>
                    </div>
                  </label>
                ))}
              </div>
            </Field>
          </Section>

          {/* Actividad */}
          <Section
            title="Actividad económica"
            description="Código CIIU principal — debe coincidir con tu RUT casilla 46."
          >
            <Field label="CIIU principal *" required help="4 dígitos.">
              <input
                type="text"
                value={form.ciiu}
                onChange={(e) =>
                  update('ciiu', e.target.value.replace(/\D/g, '').slice(0, 4))
                }
                required
                maxLength={4}
                inputMode="numeric"
                className={`${input} font-mono w-32`}
                placeholder="7020"
              />
            </Field>
            <Field label="CIIU secundarios" help="Separa con comas, hasta 3. Ej: 7110,7220">
              <input
                type="text"
                value={(form.ciiu_secondary ?? []).join(',')}
                onChange={(e) =>
                  update(
                    'ciiu_secondary',
                    e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter((s) => /^[0-9]{4}$/.test(s))
                      .slice(0, 3),
                  )
                }
                className={`${input} font-mono`}
                placeholder="7110,7220"
              />
            </Field>
          </Section>

          {/* Branding */}
          <Section
            title="Branding (PDF de factura)"
            description="Logo y color que aparecen en el PDF que tus clientes reciben."
          >
            <Field
              label="Logo"
              help="PNG, JPG, SVG o WEBP · máx 2 MB · ratio 4:1 recomendado, fondo blanco o transparente."
            >
              <div className="mt-1.5 flex items-center gap-4">
                {form.logo_url ? (
                  <img
                    src={form.logo_url}
                    alt="logo"
                    className="h-16 max-w-[160px] rounded border border-border bg-bg-app p-2 object-contain"
                  />
                ) : (
                  <div className="flex h-16 w-32 items-center justify-center rounded border border-dashed border-border bg-bg-app text-xs text-fg-muted">
                    sin logo
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) logoUpload.mutate(f)
                      e.target.value = '' // reset para permitir re-upload del mismo archivo
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={logoUpload.isPending}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-surface px-3 py-1.5 text-xs font-medium hover:bg-bg-elevated disabled:opacity-50"
                  >
                    {logoUpload.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Subiendo…
                      </>
                    ) : (
                      <>
                        <ImagePlus className="h-3.5 w-3.5" />
                        {form.logo_url ? 'Reemplazar' : 'Subir logo'}
                      </>
                    )}
                  </button>
                  {form.logo_url && (
                    <button
                      type="button"
                      onClick={() => update('logo_url', '')}
                      className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-error-fg"
                    >
                      <X className="h-3 w-3" /> Quitar
                    </button>
                  )}
                </div>
              </div>
            </Field>
            <Field label="Color primario" help="Hex con #, e.g. #ea580c (naranja Taxor).">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={form.primary_color ?? ''}
                  onChange={(e) =>
                    update('primary_color', e.target.value.toLowerCase().slice(0, 7))
                  }
                  className={`${input} font-mono w-32`}
                  placeholder="#ea580c"
                />
                {form.primary_color && /^#[0-9a-f]{6}$/.test(form.primary_color) && (
                  <div
                    className="h-9 w-9 rounded-md border border-border"
                    style={{ backgroundColor: form.primary_color }}
                    aria-label="preview"
                  />
                )}
              </div>
            </Field>
          </Section>

          {/* Recordatorios no-editables */}
          <div className="rounded-xl border border-yellow-300 bg-yellow-50 p-4 text-xs text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-800">
            <strong>No editable desde aquí:</strong> Resolución DIAN y
            Certificado ECD. Cada uno tiene su propio flow porque requieren
            validaciones especiales (alertas de vigencia, cargas de archivos).
            Llegan en próximos sprints (J.3 + J.2.5).
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3 pt-2">
            <Link
              to={ROUTES.SETTINGS}
              className="text-sm text-fg-secondary hover:text-fg-primary"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={!canSave || save.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-brand-500 to-rose-500 px-5 py-2.5 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-500/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {save.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Guardando…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" /> Guardar cambios
                </>
              )}
            </button>
          </div>
          {!canSave && (
            <p className="text-xs text-fg-muted">
              Completa los campos marcados con * para guardar.
            </p>
          )}
        </form>
      </div>
    </AppShell>
  )
}

// ─── Sub-componentes ─────────────────────────────────────────────────

const input =
  'mt-1.5 w-full rounded-md border border-border bg-bg-app px-3 py-2.5 text-sm text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20'

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl border border-border bg-bg-surface p-6">
      <header>
        <h2 className="text-lg font-semibold text-fg-primary">{title}</h2>
        {description && <p className="mt-1 text-sm text-fg-secondary">{description}</p>}
      </header>
      <div className="mt-5 space-y-4">{children}</div>
    </section>
  )
}

function Field({
  label,
  required,
  help,
  children,
}: {
  label: string
  required?: boolean
  help?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-xs font-medium uppercase tracking-wide text-fg-secondary">
        {label}
        {required && <span className="ml-1 text-error-fg">*</span>}
      </label>
      {children}
      {help && <p className="mt-1 text-xs text-fg-muted">{help}</p>}
    </div>
  )
}
